import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, Globe, ChevronDown, LogOut, LayoutDashboard, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency, CURRENCIES } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { CONTACT } from '../lib/contactInfo';
import Logo from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Header = () => {
  const { t, lang, setLang, isRTL } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { cartCount, wishlist } = useCart();
  const { user, logout, isAdmin } = useAuth();
  const { categories: CATEGORIES, products: PRODUCTS } = useData();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/shop?q=${encodeURIComponent(searchQ)}`);
      setSearchOpen(false);
      setSearchQ('');
    }
  };

  // Live-filtered suggestions as the shopper types — same match logic as the shop page's
  // full search (name AR/EN, SKU), just capped to a handful of results for the dropdown.
  const searchSuggestions = React.useMemo(() => {
    const query = searchQ.trim().toLowerCase();
    if (!query) return [];
    return PRODUCTS.filter(
      (p) =>
        p.nameAr.toLowerCase().includes(query) ||
        p.nameEn.toLowerCase().includes(query) ||
        (p.sku || '').toLowerCase().includes(query)
    ).slice(0, 6);
  }, [searchQ, PRODUCTS]);

  const goToSuggestion = (id) => {
    navigate(`/product/${id}`);
    setSearchOpen(false);
    setSearchQ('');
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F1] border-b border-ink-900/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24 md:h-32 gap-2">
          {/* Left: Menu, Scan, Search */}
          <div className="flex items-center gap-1 md:gap-2">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-2 hover:bg-ink-50 rounded-full transition-colors" aria-label="menu">
                  <Menu className="w-6 h-6 md:w-7 md:h-7 text-ink-900" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent side={isRTL ? 'right' : 'left'} className="bg-[#FAF7F1] w-80 overflow-y-auto">
                <div className="flex flex-col gap-1 mt-6 pb-6">
                  <div className="mb-4">
                    <Logo className="h-24 w-24" />
                  </div>
                  <Link to="/" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">{t('home')}</Link>
                  <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">{t('shop')}</Link>
                  {CATEGORIES.filter((c) => !c.parentId).map((main) => (
                    <div key={main.id} className="mt-1">
                      <Link to={`/category/${main.slug}`} onClick={() => setMobileMenuOpen(false)} className="py-2 px-2 block text-sm font-bold text-ink-900 hover:text-gold-500">
                        {lang === 'ar' ? main.nameAr : main.nameEn}
                      </Link>
                      {CATEGORIES.filter((c) => c.parentId === main.id).map((sub) => (
                        <Link key={sub.id} to={`/category/${sub.slug}`} onClick={() => setMobileMenuOpen(false)} className="py-1.5 px-6 block text-sm text-ink-900/70 hover:text-gold-500">
                          {lang === 'ar' ? sub.nameAr : sub.nameEn}
                        </Link>
                      ))}
                    </div>
                  ))}
                  <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10 mt-2">{t('about')}</Link>
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">{t('contact')}</Link>

                  <Link to="/account?tab=wishlist" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 flex items-center gap-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">
                    <Heart className="w-4 h-4" /> Wishlist
                    {wishlist.length > 0 && <span className="ml-auto text-xs bg-gold-500 text-white rounded-full w-5 h-5 flex items-center justify-center">{wishlist.length}</span>}
                  </Link>

                  {user ? (
                    <>
                      <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 flex items-center gap-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">
                        <User className="w-4 h-4" /> {user.name}
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="py-3 px-2 flex items-center gap-2 hover:bg-ink-50 rounded font-semibold text-ink-900 border-b border-ink-900/10">
                          <LayoutDashboard className="w-4 h-4" /> {t('admin')}
                        </Link>
                      )}
                      <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="py-3 px-2 flex items-center gap-2 hover:bg-ink-50 rounded font-semibold text-red-600 text-left">
                        <LogOut className="w-4 h-4" /> {t('logout')}
                      </button>
                    </>
                  ) : (
                    <div className="flex gap-2 mt-2">
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1 text-center border border-ink-900/30 rounded-md px-3 py-2 text-sm font-semibold">{t('login')}</Link>
                      <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="flex-1 text-center bg-ink-900 text-white rounded-md px-3 py-2 text-sm font-semibold">{t('register')}</Link>
                    </div>
                  )}

                  <button onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="flex items-center justify-center gap-1 border border-ink-900/30 rounded-md px-3 py-2 text-sm mt-4">
                    <Globe className="w-4 h-4" /> {lang === 'ar' ? 'English' : 'العربية'}
                  </button>
                </div>
              </SheetContent>
            </Sheet>

            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-ink-50 flex items-center justify-center hover:bg-ink-100 transition-colors" title="Instagram">
              <svg className="w-5 h-5 text-ink-900" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
              </svg>
            </a>

            <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 hover:bg-ink-50 rounded-full transition-colors" aria-label="search">
              <Search className="w-6 h-6 text-ink-900" strokeWidth={1.75} />
            </button>
          </div>

          {/* Center: Logo */}
          <Link to="/" className="flex items-center justify-center shrink-0">
            <Logo className="h-20 w-20 md:h-28 md:w-28" />
          </Link>

          {/* Right: Currency + Cart */}
          <div className="flex items-center gap-1.5 md:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 border-2 border-yellow-400 rounded-lg px-3.5 py-2.5 md:px-4 md:py-3 text-sm md:text-base font-semibold text-ink-900 hover:bg-ink-50 transition-colors">
                {currency} <ChevronDown className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                {Object.values(CURRENCIES).map((c) => (
                  <DropdownMenuItem key={c.code} onClick={() => setCurrency(c.code)} className={currency === c.code ? 'bg-gold-50 font-semibold' : ''}>
                    <span className="mr-2">{c.flag}</span> {c.code} - {lang === 'ar' ? c.nameAr : c.nameEn}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/cart" className="relative p-2 hover:bg-ink-50 rounded-full transition-colors">
              <ShoppingCart className="w-8 h-8 md:w-9 md:h-9 text-ink-900" strokeWidth={1.75} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Desktop nav row */}
        <nav className="hidden lg:flex items-center justify-center gap-8 h-12 border-t border-ink-900/5">
          <Link to="/" className="text-ink-900 hover:text-gold-500 font-semibold tracking-wide transition-colors">
            {t('home')}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-ink-900 hover:text-gold-500 font-semibold tracking-wide transition-colors focus:outline-none">
              {t('shop')} <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 max-h-96 overflow-y-auto">
              <DropdownMenuItem onClick={() => navigate('/shop')} className="font-semibold">
                {t('viewAll')}
              </DropdownMenuItem>
              {CATEGORIES.filter((c) => !c.parentId).map((main) => (
                <React.Fragment key={main.id}>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/category/${main.slug}`)} className="font-bold">
                    {lang === 'ar' ? main.nameAr : main.nameEn}
                  </DropdownMenuItem>
                  {CATEGORIES.filter((c) => c.parentId === main.id).map((sub) => (
                    <DropdownMenuItem key={sub.id} onClick={() => navigate(`/category/${sub.slug}`)} className="pl-6 text-ink-900/70">
                      {lang === 'ar' ? sub.nameAr : sub.nameEn}
                    </DropdownMenuItem>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/about" className="text-ink-900 hover:text-gold-500 font-semibold tracking-wide transition-colors">
            {t('about')}
          </Link>
          <Link to="/contact" className="text-ink-900 hover:text-gold-500 font-semibold tracking-wide transition-colors">
            {t('contact')}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-ink-900 hover:text-gold-500 font-semibold tracking-wide transition-colors">
                <User className="w-4 h-4" /> {user ? user.name : t('login')}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {user ? (
                <>
                  <div className="px-2 py-2 text-sm">
                    <div className="font-semibold text-ink-900">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')}>
                    <User className="w-4 h-4 mr-2" /> {t('myAccount')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account?tab=orders')}>
                    <ShoppingCart className="w-4 h-4 mr-2" /> {t('myOrders')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> {t('admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => navigate('/login')}>{t('login')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/register')}>{t('register')}</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            className="flex items-center gap-1 border border-ink-900/15 rounded-md px-3 py-1.5 text-sm text-ink-900 hover:bg-ink-50 transition-colors"
          >
            <Globe className="w-4 h-4" /> {lang === 'ar' ? 'EN' : 'عر'}
          </button>
        </nav>

        {/* Search overlay */}
        {searchOpen && (
          <div className="border-t border-ink-900/10 py-4 animate-in slide-in-from-top duration-200">
            <div className="relative max-w-3xl mx-auto">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  autoFocus
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="flex-1 border-ink-900/20 focus:border-ink-900 bg-white"
                />
                <Button type="submit" className="bg-ink-900 hover:bg-black">{t('search')}</Button>
                <Button type="button" variant="ghost" onClick={() => setSearchOpen(false)}><X className="w-4 h-4" /></Button>
              </form>

              {searchSuggestions.length > 0 && (
                <div className="absolute top-full inset-x-0 mt-1.5 bg-white border border-ink-900/10 rounded-lg shadow-lg overflow-hidden z-10">
                  {searchSuggestions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => goToSuggestion(p.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-ink-50 transition-colors text-left"
                    >
                      <img src={p.image} alt="" className="w-9 h-9 rounded-md object-cover bg-[#F5F5F3] shrink-0" />
                      <span className="text-sm text-ink-900 line-clamp-1">{lang === 'ar' ? p.nameAr : p.nameEn}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
